import type { IconSvgElement } from '@hugeicons/react-native';
import { HugeiconsIcon } from '@hugeicons/react-native';
import React, { type ReactNode } from 'react';
import {
  Pressable,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { Colors } from '../constants/Colors';
import { TextBold, TextReg } from './StyledText';

type Size = 'small' | 'medium' | 'large';

type Props = {
  title: string;
  description?: string;
  icon?: IconSvgElement;
  isSelected: boolean;
  onPress: () => void;
  size?: Size;
  children?: ReactNode;
  style?: StyleProp<ViewStyle>;
};

const sizeConfig = {
  small: {
    padding: 12,
    iconContainerSize: 36,
    iconSize: 20,
    titleFontSize: 14,
    descriptionFontSize: 12,
    gap: 12,
  },
  medium: {
    padding: 16,
    iconContainerSize: 40,
    iconSize: 24,
    titleFontSize: 16,
    descriptionFontSize: 13,
    gap: 12,
  },
  large: {
    padding: 24,
    iconContainerSize: 56,
    iconSize: 32,
    titleFontSize: 20,
    descriptionFontSize: 15,
    gap: 16,
  },
};

export function SelectableCard({
  title,
  description,
  icon,
  isSelected,
  onPress,
  size = 'medium',
  children,
  style,
}: Props) {
  const config = sizeConfig[size];

  const isSmall = size === 'small';

  return (
    <Pressable
      style={[
        styles.card,
        {
          padding: config.padding,
          gap: config.gap,
        },
        isSmall && styles.cardSmall,
        isSelected && styles.cardSelected,
        style,
      ]}
      onPress={onPress}
    >
      <View style={[styles.header, isSmall && styles.headerSmall]}>
        {icon && (
          <View
            style={[
              styles.iconContainer,
              {
                width: config.iconContainerSize,
                height: config.iconContainerSize,
                borderRadius: config.iconContainerSize / 2,
              },
            ]}
          >
            <HugeiconsIcon
              icon={icon}
              size={config.iconSize}
              color={Colors.text}
            />
          </View>
        )}
        <TextBold
          style={{
            fontSize: config.titleFontSize,
            flex: isSmall ? undefined : 1,
          }}
        >
          {title}
        </TextBold>
        {isSmall && description && (
          <TextReg
            style={[
              styles.descriptionSmall,
              { fontSize: config.descriptionFontSize },
            ]}
          >
            {description}
          </TextReg>
        )}
      </View>

      {!isSmall && description && (
        <TextReg
          style={[styles.description, { fontSize: config.descriptionFontSize }]}
        >
          {description}
        </TextReg>
      )}

      {children}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    backgroundColor: Colors.opacity05,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  cardSmall: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardSelected: {
    borderColor: Colors.purple1,
    backgroundColor: Colors.opacity10,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerSmall: {
    flex: 1,
  },
  iconContainer: {
    backgroundColor: Colors.opacity20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  description: {
    color: Colors.text,
    opacity: 0.8,
    lineHeight: 22,
  },
  descriptionSmall: {
    color: Colors.text,
    opacity: 0.7,
    marginLeft: 'auto',
  },
});
